import { roundAwayFromZero } from "~/utils/round-away-from-zero";

import { calculateProportionalDiscount } from "./calculate-proportional-discount";
import { distributeUnitPriceAllowance } from "./distribute-unit-price-allowance";

// `currency` = per-unit × qty; `flat` = absolute line reduction (POS manual
// discountUnitPrice). unitPrice-allowance discounts live in `allowanceDiscountIds`.
export type SaleEngineLineDiscount =
  | { kind: "percentage"; amount: number }
  | { kind: "currency"; amount: number }
  | { kind: "flat"; amount: number };

export type SaleEngineLine = {
  basketId: string;
  quantity: number;
  /** Per-unit incl-VAT composite (rounded base + variants), pre-discount. */
  unitCompositeInclVat: number;
  /**
   * Whether this line shares an order discount. Caller sets channel policy —
   * POS excludes zero-base products (free base + paid options).
   */
  eligibleForOrderDiscount: boolean;
  /** Per-line discounts, application order (no unitPrice-allowance discounts). */
  discounts: SaleEngineLineDiscount[];
  /** Hybrid-billing discount id(s) gating unitPrice-allowance eligibility. */
  allowanceDiscountIds: string[];
  /** Per-unit, incl VAT. Never part of `unitCompositeInclVat`. */
  unitDepositInclVat?: number;
};

export type SaleEngineOptions = {
  allowances: {
    discountId: string;
    type: string;
    remaining: number;
    isAllowanceOver: boolean;
  }[];
  /** Backing discount defs — resolve a unitPrice allowance's amount. */
  discounts: {
    id: string;
    amount: number;
    amountType: "currency" | "percentage";
  }[];
  orderDiscount?: { amount: number; type: "percentage" | "currency" } | null;
  /** Allowances may cover the deposit as well as the product. */
  absorbDeposit?: boolean;
};

export type SaleEnginePricedLine = {
  basketId: string;
  /** Line total before any discount (`unitCompositeInclVat × quantity`). */
  lineTotalBeforeDiscount: number;
  /** Total per-line discount applied (incl VAT). */
  perLineDiscount: number;
  /** The `flat` discounts actually taken, after the cap at the line. */
  flatDiscount: number;
  /** Pre-per-line-discount total if any applied, else null (strikethrough basis). */
  unitListPrice: number | null;
  /** Capped unitPrice-allowance deduction. */
  allowanceDeduction: number;
  /** This line's share of the order discount. */
  orderDiscount: number;
  /** Final line total (incl VAT), rounded once at the line sum. */
  lineTotal: number;
  /** Never reduced by a discount stage. */
  depositTotal: number;
  /** Part of `depositTotal` an allowance covered. Zero unless `absorbDeposit`. */
  depositAllowanceDeduction: number;
};

/** Split a line's allowance share between its product money and its deposit. */
function splitAllowanceShare(
  share: number,
  productTotal: number,
  depositTotal: number,
  absorbDeposit: boolean
): { onProduct: number; onDeposit: number } {
  // Not absorbing: the allowance was distributed over product money alone, so
  // all of it lands there, exactly as before deposits existed.
  if (!absorbDeposit) return { onProduct: share, onDeposit: 0 };

  // Product money first, whatever remains covers the deposit. Both ends are
  // capped: a line discounted below zero must not report a negative deduction,
  // and the proportional split's residual lands on the first line, which would
  // otherwise absorb a deposit that line never carried.
  const onProduct = Math.min(share, Math.max(productTotal, 0));

  return {
    onProduct,
    onDeposit: Math.min(share - onProduct, depositTotal)
  };
}

/** Resolve a per-line discount to a money amount. Currency is per-unit × qty. */
function resolveLineDiscountAmount(
  discount: SaleEngineLineDiscount,
  runningTotal: number,
  quantity: number
): number {
  if (discount.kind === "percentage") {
    return runningTotal * (discount.amount / 100);
  }
  if (discount.kind === "currency") {
    return discount.amount * quantity;
  }
  // A manual discount only removes what the line still has.
  return Math.min(discount.amount, Math.max(runningTotal, 0));
}

function lineTotalBeforeDiscount(line: SaleEngineLine): number {
  return line.unitCompositeInclVat * line.quantity;
}

/** A negative-priced line adds nothing to what an order discount can take. */
function isOrderDiscountable(line: SaleEngineLine): boolean {
  return line.eligibleForOrderDiscount && lineTotalBeforeDiscount(line) > 0;
}

/**
 * The money an order discount can take, unrounded like the flat cap so a .5
 * line cannot round to -1. The order-discount modal guards against this total.
 */
export function orderDiscountableTotal(lines: SaleEngineLine[]): number {
  return lines
    .filter(isOrderDiscountable)
    .reduce((acc, line) => acc + lineTotalBeforeDiscount(line), 0);
}

/**
 * Shared basket pricing for POS and kiosk: per-line discounts → unitPrice
 * allowance (capped + distributed) → order discount → finalize. Caller maps its
 * basket to `SaleEngineLine[]` and projects the result back.
 *
 * POS semantics: discounts subtracted unrounded, rounded once at the line sum;
 * currency discount is per-unit × qty. (Kiosk used to round per step and treat
 * currency as flat — unified onto POS, which matches the server.)
 *
 * No channel policy here — caller shapes the input (e.g. POS drops per-line
 * discounts when an order discount applies).
 */
export function computeBasket(
  lines: SaleEngineLine[],
  options?: SaleEngineOptions
): SaleEnginePricedLine[] {
  const allowances = options?.allowances ?? [];
  const discounts = options?.discounts ?? [];
  const orderDiscount = options?.orderDiscount;
  const absorbDeposit = options?.absorbDeposit ?? false;

  // Pre-discount total, then per-line discount fold.
  const staged = lines.map((line) => {
    const before = lineTotalBeforeDiscount(line);

    let running = before;
    let flatDiscount = 0;
    for (const discount of line.discounts) {
      const amount = resolveLineDiscountAmount(
        discount,
        running,
        line.quantity
      );
      running -= amount;
      if (discount.kind === "flat") flatDiscount += amount;
    }

    return {
      line,
      before,
      afterPerLine: running,
      perLineDiscount: before - running,
      flatDiscount,
      unitListPrice: line.discounts.length > 0 ? before : null,
      depositTotal: (line.unitDepositInclVat ?? 0) * line.quantity
    };
  });

  // unitPrice allowances — capped + distributed over the post-per-line totals.
  const allowanceByBasket = new Map<string, number>();
  const activeAllowances = allowances.filter(
    (allowance) => allowance.type === "unitPrice" && !allowance.isAllowanceOver
  );

  for (const allowance of activeAllowances) {
    const discount = discounts.find((d) => d.id === allowance.discountId);
    if (!discount) continue;

    const eligible = staged.filter((s) =>
      s.line.allowanceDiscountIds.includes(allowance.discountId)
    );

    const distributed = distributeUnitPriceAllowance({
      lines: eligible.map((s) => ({
        basketId: s.line.basketId,
        totalPrice: absorbDeposit
          ? s.afterPerLine + s.depositTotal
          : s.afterPerLine,
        quantity: s.line.quantity
      })),
      remaining: allowance.remaining,
      discount
    });

    for (const { basketId, amount } of distributed) {
      allowanceByBasket.set(
        basketId,
        (allowanceByBasket.get(basketId) ?? 0) + amount
      );
    }
  }

  // Order discount — proportional over pre-discount totals.
  const orderByBasket = new Map<string, number>();
  if (orderDiscount?.amount) {
    const totalUnit = staged.reduce(
      (acc, s) => acc + roundAwayFromZero(s.before),
      0
    );
    const requestedDiscount =
      orderDiscount.type === "percentage"
        ? totalUnit * (orderDiscount.amount / 100)
        : orderDiscount.amount;

    // The discount never removes more than the discountable lines hold.
    const discountable = staged.filter((s) => isOrderDiscountable(s.line));
    const discountableTotal = orderDiscountableTotal(lines);
    const totalDiscount = Math.min(requestedDiscount, discountableTotal);

    const distributed = calculateProportionalDiscount(
      discountable.map((s) => ({
        basketId: s.line.basketId,
        totalPrice: s.before
      })),
      discountableTotal,
      totalDiscount
    );

    for (const { basketId, amount } of distributed) {
      orderByBasket.set(basketId, amount);
    }
  }

  // Finalize each line: round once at the sum.
  return staged.map((s) => {
    const {
      onProduct: allowanceDeduction,
      onDeposit: depositAllowanceDeduction
    } = splitAllowanceShare(
      allowanceByBasket.get(s.line.basketId) ?? 0,
      s.afterPerLine,
      s.depositTotal,
      absorbDeposit
    );
    const orderDiscount = orderByBasket.get(s.line.basketId) ?? 0;
    const raw = s.afterPerLine - allowanceDeduction - orderDiscount;

    return {
      basketId: s.line.basketId,
      lineTotalBeforeDiscount: s.before,
      perLineDiscount: s.perLineDiscount,
      flatDiscount: s.flatDiscount,
      unitListPrice: s.unitListPrice,
      allowanceDeduction,
      orderDiscount,
      lineTotal: roundAwayFromZero(raw),
      depositTotal: s.depositTotal,
      depositAllowanceDeduction
    };
  });
}
